import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials, faqData } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 奇妙漫画网';
  }, []);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于奇妙漫画网',
    description: '奇妙漫画网提供韩漫免费观看在线阅读漫画，韩国漫画免费漫画大全，一站式阅读体验。了解我们的品牌故事、口碑评价和联系方式。',
    url: 'https://www.qimiaomanhua.com/about',
    about: {
      '@type': 'Organization',
      name: '奇妙漫画网',
      foundingDate: '2020',
      description: '奇妙漫画网提供韩漫免费观看在线阅读漫画，韩国漫画免费漫画大全。',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'contact@qimiaomanhua.com',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.qimiaomanhua.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.qimiaomanhua.com/about' },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">
            <Breadcrumb items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]} />
          </div>

          {/* Hero */}
          <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cozy%20reading%20room%20with%20bookshelves%20filled%20with%20colorful%20comic%20books%2C%20soft%20natural%20light%20streaming%20through%20window%2C%20comfortable%20armchair%2C%20artistic%20stylized%20illustration%2C%20warm%20amber%20and%20cream%20tones%2C%20inviting%20atmosphere%2C%20clean%20composition&width=1600&height=800&seq=about-hero-bg&orientation=landscape"
              alt="关于奇妙漫画网"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-3">关于奇妙漫画网</h1>
              <p className="text-sm md:text-base text-white/80 max-w-xl">韩漫免费观看在线阅读漫画平台</p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">品牌故事</h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    奇妙漫画网成立于2020年，是国内领先的韩漫免费观看在线阅读漫画平台。我们致力于为全球华人漫画爱好者提供最优质的韩国漫画免费漫画大全服务。
                  </p>
                  <p>
                    自创立以来，奇妙漫画网始终坚持"免费、高清、及时"的服务理念，收录了数千部优质韩国漫画和日本漫画作品。无论是热门连载还是经典完结作品，用户都能在这里找到心仪的漫画资源。
                  </p>
                  <p>
                    我们的团队由资深漫画爱好者和技术专家组成，专注于为用户打造流畅舒适的漫画在线阅读体验。从画质优化到页面加载速度，从分类导航到搜索功能，每一个细节都倾注了我们对漫画的热爱。
                  </p>
                  <p>
                    如今，奇妙漫画网已成为数百万漫画爱好者的首选阅读平台。我们每天更新大量最新章节，确保读者能够第一时间追更喜爱的作品。未来，我们将继续拓展漫画资源库，为读者带来更多精彩内容。
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-heading font-bold text-primary-500 mb-1">2020</div>
                  <p className="text-xs text-foreground-500">年创立</p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-heading font-bold text-primary-500 mb-1">5000+</div>
                  <p className="text-xs text-foreground-500">部漫画资源</p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-heading font-bold text-primary-500 mb-1">300万+</div>
                  <p className="text-xs text-foreground-500">月活跃读者</p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-heading font-bold text-primary-500 mb-1">99.9%</div>
                  <p className="text-xs text-foreground-500">服务可用率</p>
                </div>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="bg-background-100 py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">我们的优势</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-background-50 rounded-xl p-6 border border-background-200 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-base font-medium text-foreground-900 mb-2">完全免费</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">所有漫画资源永久免费，无需付费即可畅享海量内容</p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-background-200 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600">
                    <i className="ri-speed-up-line text-2xl"></i>
                  </div>
                  <h3 className="text-base font-medium text-foreground-900 mb-2">极速更新</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">热门漫画第一时间同步更新，追更不等待</p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-background-200 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-600">
                    <i className="ri-image-line text-2xl"></i>
                  </div>
                  <h3 className="text-base font-medium text-foreground-900 mb-2">高清画质</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">专业图片优化处理，保证画质清晰细腻，阅读体验出色</p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-background-200 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
                    <i className="ri-smartphone-line text-2xl"></i>
                  </div>
                  <h3 className="text-base font-medium text-foreground-900 mb-2">多端适配</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">支持PC、平板、手机浏览，随时随地畅享阅读</p>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-2">用户口碑</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">来自漫画爱好者的真实评价</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map(test => (
                <div key={test.id} className="bg-background-50 rounded-xl border border-background-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={test.avatar} alt={test.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{test.name}</p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="w-3 h-3 flex items-center justify-center">
                            <i className={`text-xs ${i < test.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed">{test.content}</p>
                  <p className="text-xs text-foreground-400 mt-2">{test.date}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="bg-background-100 py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">联系我们</h2>
                  <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
                    如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们的客服团队将在工作日尽快回复您。
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                        <p className="text-xs text-foreground-500">contact@qimiaomanhua.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-customer-service-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">在线客服</p>
                        <p className="text-xs text-foreground-500">工作日 9:00 - 18:00（北京时间）</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">公司地址</p>
                        <p className="text-xs text-foreground-500">中国·北京市朝阳区</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">在线留言</h2>
                  <form
                    data-readdy-form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
                      if (honeypot && honeypot.value.trim()) {
                        setFormStatus('success');
                        setTimeout(() => setFormStatus('idle'), 3000);
                        return;
                      }
                      const formData = new FormData(form);
                      try {
                        const res = await fetch('https://readdy.ai/api/form/d9p0raqftmtnfcfh0fhg', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                        });
                        const text = await res.text();
                        let parsed;
                        try { parsed = JSON.parse(text); } catch { parsed = null; }
                        if (res.ok && parsed?.code === 'OK') {
                          setFormStatus('success');
                          form.reset();
                          setTimeout(() => setFormStatus('idle'), 3000);
                        } else {
                          const msg = parsed?.meta?.message || parsed?.message || text || '提交失败';
                          setFormError(msg);
                          setFormStatus('error');
                        }
                      } catch {
                        setFormError('网络错误，请稍后再试');
                        setFormStatus('error');
                      }
                    }}
                    className="space-y-4"
                  >
                    <div>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="您的姓名"
                        className="w-full px-4 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="您的邮箱"
                        className="w-full px-4 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        placeholder="请输入留言内容（最多500字）"
                        className="w-full px-4 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      提交留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 text-center">留言已成功提交，感谢您的反馈！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError || '提交失败，请稍后再试'}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">常见问题</h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqData.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-xl border border-background-200 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer select-none">
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform duration-200">
                      <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}